import { computed, ref, unref, watch } from 'vue'
import { FilterMatchMode } from 'primevue/api'
import type {
  DataTableExpandedRows,
  DataTableFilterMeta,
} from 'primevue/datatable'
import { useConfirm } from 'primevue/useconfirm'
import dayjs from 'dayjs'
import useNotifications from '@/store/notification'
import useLoading from '@/store/load'
import { getEmployeeById } from '../services/EmployeeService'
import {
  GetPTOPreferences,
  createVacationRequest,
  deleteVacationById,
  getDateEnd,
  getDaysByEmployeeId,
  getVacationReceipt,
  getVacationsByEmployeeId,
} from '../services/vacationService'
import { getLicensesByEmployeeId } from '../services/LicenseService'
import { getApprovalFlowStepsByRequest } from '@/modules/approvers/services/ApprovalRequestService'
import { getFileUrlPresigned } from '@/shared/services/fileService'
import { formatDate } from '@/shared/utils/formatDate'
import type Vacation from '../models/Vacation'
import type License from '../models/License'
import type { WorkingDay } from '../models/WokingDay'
import type { Holiday } from '../models/Holiday'
import type CreateVacationInputDto from '../services/interfaces/CreateVacationInputDto'
import type { EmployeeDetail } from '../models/EmployeeDetail'
import type { ComputedRef, Ref } from 'vue'

interface UseVacationsOptions {
  employeeId?: Ref<string | undefined> | ComputedRef<string | undefined>
  userId?: Ref<string | undefined> | ComputedRef<string | undefined>
  fromRRHH?: Ref<boolean> | ComputedRef<boolean>
  autoLoad?: boolean
}

const defaultFilters = (): DataTableFilterMeta => ({
  globalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
})

const buildVacationForm = (
  employeeId?: string,
  origin?: string
): CreateVacationInputDto => ({
  dateInit: undefined,
  dateEnd: undefined,
  quantityDays: 0,
  origin: origin ?? 'NATIVE',
  observation: '',
  employeeId: employeeId ?? '',
  file: null,
  daysDetail: [],
  isPaid: false,
})

export function useVacations(options: UseVacationsOptions = {}) {
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const employee = ref<EmployeeDetail>()
  const vacations = ref<Vacation[]>([])
  const licenses = ref<License[]>([])
  const statuses = ref(['PENDING', 'APPROVED', 'REJECTED'])
  const vacationDialog = ref(false)
  const submitted = ref(false)
  const vacationBalance = ref(0)
  const vacationsEnjoyed = ref(0)
  const workinDays = ref<WorkingDay[]>([])
  const holidays = ref<Holiday[]>([])
  const vacationsPending = ref<Date[]>([])
  const vacationsApproved = ref<Date[]>([])
  const licensesApproved = ref<Date[]>([])
  const licensesPending = ref<Date[]>([])
  const allowPayedVacations = ref(false)
  const allowNegativePTOBalance = ref(false)
  const filters = ref<DataTableFilterMeta>(defaultFilters())
  const expandedRows = ref<DataTableExpandedRows | any[]>([] as any[])
  const vacation = ref<CreateVacationInputDto>(buildVacationForm())

  const resolveEmployeeId = (override?: string) =>
    override ?? unref(options.employeeId)

  const resetVacationState = () => {
    vacations.value = []
    licenses.value = []
    workinDays.value = []
    holidays.value = []
    vacationsPending.value = []
    vacationsApproved.value = []
    licensesApproved.value = []
    licensesPending.value = []
    allowPayedVacations.value = false
    vacationBalance.value = 0
    vacationDialog.value = false
    submitted.value = false
    filters.value = defaultFilters()
    expandedRows.value = [] as any[]
    vacation.value = buildVacationForm(resolveEmployeeId())
  }

  const refreshData = async (employeeIdOverride?: string) => {
    const employeeId = resolveEmployeeId(employeeIdOverride)
    if (!employeeId) {
      resetVacationState()
      employee.value = undefined
      return
    }

    loadingStore.setLoadingState()
    try {
      const [employeeResponse, vacationResponse, licensesResponse] =
        await Promise.all([
          getEmployeeById(employeeId),
          getVacationsByEmployeeId(employeeId),
          getLicensesByEmployeeId(employeeId),
        ])

      if (!employeeResponse || !vacationResponse || !licensesResponse) {
        return
      }

      employee.value = employeeResponse
      vacationBalance.value = employeeResponse.vacationBalance ?? 0
      vacation.value = buildVacationForm(
        resolveEmployeeId(employeeIdOverride),
        options.fromRRHH ? 'RRHH' : 'NATIVE'
      )
      filters.value = defaultFilters()
      expandedRows.value = [] as any[]
      submitted.value = false
      vacationDialog.value = false

      vacations.value = vacationResponse.data.map((v) => ({
        ...v,
        dateInit: formatDate(v.dateInit),
        dateEnd: formatDate(v.dateEnd),
        createdAt: dayjs(v.createdAt).format('DD/MM/YYYY HH:mm'),
      }))

      licenses.value = licensesResponse.data.map((l) => ({
        ...l,
        dateInit: formatDate(l.dateInit),
        dateEnd: formatDate(l.dateEnd),
        createdAt: formatDate(l.createdAt),
      }))

      vacationsEnjoyed.value = vacationResponse.data.reduce((acc, v) => {
        if (v.globalStatus === 'APPROVED') {
          return acc + (v.quantity ?? v.quantity ?? 0)
        }
        return acc
      }, 0)
    } catch (error) {
      console.error('Failed to refresh vacation data', error)
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const onFiltersUpdate = (value: DataTableFilterMeta) => {
    filters.value = value
  }

  const onExpandedRowsUpdate = (rows: DataTableExpandedRows | any[]) => {
    expandedRows.value = rows
  }

  const setVacationDialogVisibility = (value: boolean) => {
    vacationDialog.value = value
  }

  const saveFile = (event: any) => {
    const file = event?.files?.[0]
    if (!file) return
    vacation.value.file = file
  }

  const removeFile = () => {
    vacation.value.file = null
  }

  const ensureWorkingDays = async () => {
    if (workinDays.value.length && holidays.value.length) {
      return
    }

    const employeeId = resolveEmployeeId()
    if (!employeeId) {
      return
    }

    const response = await getDaysByEmployeeId(employeeId)
    workinDays.value = response.workingDays
    holidays.value = response.holidays
  }

  const openNewRequestDialog = async () => {
    vacation.value = buildVacationForm(
      resolveEmployeeId(),
      options.fromRRHH ? 'RRHH' : 'NATIVE'
    )
    vacationDialog.value = true
    await ensureWorkingDays()
  }

  const deleteConfirm = (event: MouseEvent, id: string) => {
    confirm.require({
      target: event.currentTarget as HTMLElement,
      message: '¿Estás  seguro de eliminar esta solicitud?',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger p-button-sm',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        const response = await deleteVacationById(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: '200',
            severity: 'success',
            summary: 'Solicitud de vacaciones eliminada exitosamente',
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const onDeleteRequest = (payload: {
    originalEvent: MouseEvent
    requestId: string
  }) => {
    deleteConfirm(payload.originalEvent, payload.requestId)
  }

  const onRowExpand = async (event: any) => {
    if (event?.data?.origin !== 'NATIVE') {
      return
    }

    const employeeId = resolveEmployeeId()
    if (!employeeId) {
      return
    }

    const requests = await getApprovalFlowStepsByRequest({
      approvalFlowId: event.data.approvalFlowId,
      approvalFlowRequestId: event.data.approvalFlowRequestId,
      employeeId,
    })

    vacations.value = vacations.value?.map((vacationItem: any) =>
      vacationItem.id === event.data.id
        ? {
            ...vacationItem,
            steps: requests,
          }
        : vacationItem
    )
  }

  const downloadAttachment = async (fileKey: string) => {
    if (!fileKey) return

    if (fileKey.includes('http')) {
      window.open(fileKey, '_blank')
      return
    }

    const fileUrl = await getFileUrlPresigned(fileKey)
    window.open(fileUrl, '_blank')
  }

  const onVacationReceipt = async (vacationId: string) => {
    loadingStore.setLoadingState()
    try {
      const vacationBuffer = await getVacationReceipt(vacationId)
      const url = window.URL.createObjectURL(vacationBuffer)
      window.open(url, '_blank')
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const hideDialog = () => {
    vacation.value = buildVacationForm(
      resolveEmployeeId(),
      options.fromRRHH ? 'RRHH' : 'NATIVE'
    )
    vacationDialog.value = false
    submitted.value = false
  }

  const updateVacation = (value: CreateVacationInputDto) => {
    vacation.value = value
  }

  const calculateDateEnd = async () => {
    if (
      !vacation.value.quantityDays ||
      !vacation.value.dateInit ||
      vacation.value.dateInit.getTime() <= 0
    ) {
      return
    }

    loadingStore.setLoadingState()
    try {
      const response = await getDateEnd({
        days: vacation.value.quantityDays,
        startDate: vacation.value.dateInit.toISOString().split('T')[0],
        employeeId: resolveEmployeeId() || '',
      })

      vacation.value.dateEnd = new Date(`${response.endDate}T00:00:00`)

      const unavailableDates = [
        ...vacationsPending.value,
        ...vacationsApproved.value,
        ...licensesPending.value,
        ...licensesApproved.value,
      ].map((date) => date.toISOString().split('T')[0])

      if (response.dayToDay.some((date) => unavailableDates.includes(date))) {
        notificationStore.addSuccesNotification({
          detail: '400',
          severity: 'warn',
          summary:
            'Hay fechas no disponibles en el rango de fechas seleccionado',
        })

        vacation.value.dateEnd = undefined
        vacation.value.quantityDays = 0
      }

      vacation.value.daysDetail = response.dayToDay
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const createRequest = async () => {
    loadingStore.setLoadingState()
    try {
      const response = await createVacationRequest(vacation.value)
      if (response === 'OK') {
        notificationStore.addSuccesNotification({
          detail: '200',
          severity: 'success',
          summary: 'Solicitud de vacaciones creada exitosamente',
        })

        await refreshData()
      }
    } finally {
      loadingStore.dismissLoadingState()
      hideDialog()
    }
  }

  const pendingBalance = computed(() => {
    return (
      vacations.value
        ?.filter((v) => v.globalStatus === 'PENDING')
        .reduce((acc, v) => acc + (v.quantity ?? v.quantity ?? 0), 0) ??
      vacationBalance.value
    )
  })

  const saveRequest = async () => {
    submitted.value = true
    if (!isValidToSave.value) {
      return
    }

    const newBalance =
      vacationBalance.value -
      (pendingBalance.value + vacation.value.quantityDays)

    if (
      !allowNegativePTOBalance.value &&
      newBalance < 0 &&
      !vacation.value.isPaid
    ) {
      notificationStore.addInfoNotification({
        detail: '400',
        severity: 'warn',
        summary:
          'No se puede crear la solicitud de vacaciones, el saldo de días de vacaciones disponibles no es suficiente.',
      })
      return
    }

    if (
      allowNegativePTOBalance.value &&
      newBalance < 0 &&
      !vacation.value.isPaid
    ) {
      confirm.require({
        message: `Atención: esta solicitud excede tu saldo disponible de vacaciones. Saldo actual: ${vacationBalance.value} días, pendientes: ${pendingBalance.value} días, solicitados: ${vacation.value.quantityDays} días, saldo proyectado: ${newBalance} días. ¿Deseas continuar con la solicitud?`,
        header: 'Confirmación',
        group: 'negativeBalanceCF',
        icon: 'pi pi-exclamation-circle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Aceptar',
        rejectLabel: 'Cancelar',
        accept: async () => await createRequest(),
        reject: () => {},
      })

      return
    }

    await createRequest()
  }

  const getBadge = (dateRaw: any) => {
    const date = new Date(dateRaw.year, dateRaw.month, dateRaw.day)
    const timestamp = date.getTime()

    if (
      vacationsPending.value.some(
        (pendingDate) => pendingDate.getTime() === timestamp
      )
    ) {
      return 'vacation-pending'
    }

    if (
      vacationsApproved.value.some(
        (approvedDate) => approvedDate.getTime() === timestamp
      )
    ) {
      return 'vacation-approved'
    }

    if (
      licensesPending.value.some(
        (pendingDate) => pendingDate.getTime() === timestamp
      )
    ) {
      return 'license-pending'
    }

    if (
      licensesApproved.value.some(
        (approvedDate) => approvedDate.getTime() === timestamp
      )
    ) {
      return 'license-approved'
    }

    const dateString = date.toISOString().split('T')[0]

    if (
      holidays.value.some(
        (holiday) => holiday.date.split('T')[0] === dateString
      )
    ) {
      return 'holiday'
    }

    return ''
  }

  const getDisabledDays = computed(() => {
    const allDays = [0, 1, 2, 3, 4, 5, 6]

    workinDays.value.forEach((day) => {
      const normalizedDay = day.isoWeekDay === 7 ? 0 : day.isoWeekDay
      if (
        typeof normalizedDay === 'number' &&
        allDays.includes(normalizedDay)
      ) {
        const index = allDays.indexOf(normalizedDay)
        if (index !== -1) {
          allDays.splice(index, 1)
        }
      }
    })

    return allDays
  })

  const getDisabledDates = computed(() => {
    const disabledDates: Date[] = []
    vacationsPending.value = []
    vacationsApproved.value = []
    licensesPending.value = []
    licensesApproved.value = []

    holidays.value.forEach((holiday) => {
      disabledDates.push(new Date(`${holiday.date.split('T')[0]}T00:00:00`))
    })

    vacations.value
      ?.filter(
        (vacationItem) =>
          vacationItem.globalStatus === 'PENDING' &&
          vacationItem.isPaid === false
      )
      .forEach((vacationItem) => {
        vacationItem.daysDetail?.forEach((day) => {
          const date = new Date(`${day}T00:00:00`)
          disabledDates.push(date)
          vacationsPending.value.push(date)
        })
      })

    vacations.value
      ?.filter(
        (vacationItem) =>
          vacationItem.globalStatus === 'APPROVED' &&
          vacationItem.isPaid === false
      )
      .forEach((vacationItem) => {
        vacationItem.daysDetail?.forEach((day) => {
          const date = new Date(`${day}T00:00:00`)
          disabledDates.push(date)
          vacationsApproved.value.push(date)
        })
      })

    licenses.value
      ?.filter((licenseItem) => licenseItem.globalStatus === 'PENDING')
      .forEach((licenseItem) => {
        licenseItem.daysDetail?.forEach((day) => {
          const date = new Date(`${day}T00:00:00`)
          disabledDates.push(date)
          licensesPending.value.push(date)
        })
      })

    licenses.value
      ?.filter((licenseItem) => licenseItem.globalStatus === 'APPROVED')
      .forEach((licenseItem) => {
        licenseItem.daysDetail?.forEach((day) => {
          const date = new Date(`${day}T00:00:00`)
          disabledDates.push(date)
          licensesApproved.value.push(date)
        })
      })

    return disabledDates
  })

  const isValidToSave = computed(() => {
    return Boolean(
      vacation.value.dateInit &&
        vacation.value.dateEnd &&
        vacation.value.quantityDays > 0 &&
        (vacation.value.observation?.trim().length ?? 0) > 0
    )
  })

  if (options.autoLoad !== false && options.employeeId) {
    watch(
      () => unref(options.employeeId),
      (employeeId) => {
        void refreshData(employeeId ?? undefined)
      },
      { immediate: true }
    )
  }

  return {
    employee,
    vacations,
    licenses,
    statuses,
    vacationDialog,
    submitted,
    vacationBalance,
    vacationsEnjoyed,
    workinDays,
    holidays,
    vacationsPending,
    vacationsApproved,
    licensesApproved,
    licensesPending,
    allowPayedVacations,
    allowNegativePTOBalance,
    filters,
    expandedRows,
    vacation,
    pendingBalance,
    getBadge,
    getDisabledDays,
    getDisabledDates,
    isValidToSave,
    onFiltersUpdate,
    onExpandedRowsUpdate,
    setVacationDialogVisibility,
    openNewRequestDialog,
    onRowExpand,
    onVacationReceipt,
    downloadAttachment,
    onDeleteRequest,
    saveFile,
    removeFile,
    calculateDateEnd,
    saveRequest,
    hideDialog,
    updateVacation,
    refreshData,
  }
}
